<template>
  <div class="grid-item grid-item-data product_list-item">
    <slot name="slot-column"></slot>
    <div class="fav">
      <a
        href="#"
        @click.prevent="switchFavourite"
        :class="{ disabled: !$auth.loggedIn }"
        :title="isFavourite ? $t('remove_favourites') : $t('add_favourites')"
      >
        <i class="svg-icon star" :class="{ 'star-favourite': isFavourite, 'disabled': !$auth.loggedIn }"></i>
      </a>
    </div>

    <div class="articul">
      <span>{{ product.code }}</span>
    </div>

    <div class="brand">
      <span>{{ product.brand_name }}</span>
    </div>

    <div class="name">
      <nuxt-link class="link-ext" :to="localePath('/product/' + product.slug)">
        <span v-if="productLocaleData.name" v-html="noWrapHyphen(productLocaleData.name)" />
        <span v-else>{{ product.code }}</span>
      </nuxt-link>
    </div>

    <div class="stock-container">
      <template
        v-for="(stockType, idx) in ['available', 'transit', 'preorder']"
        v-if="product.amount_free[stockType] > 0 || (stockType === 'preorder' && !canShowSuppliers(product))"
      >
        <template v-if="!checkPreorder(stockType) && (!hidePriceOnRequest || (hidePriceOnRequest && product.price > 0))">
          <!-- Название склада -->
          <div class="store-name" :class="`store-${stockType}`">
            <!-- <div class="title-col hide-desktop">{{ $t('stock_pcs') }}</div> -->
            <!-- <template v-if="!$hideStockAvailabilityColumns()"> -->
            <template v-if="showStockAvailabilityColumns()">
              <template v-if="stockType === 'available'">{{ $t('available') }}</template>
              <span
                v-else
                :class="{ pointer: !checkPreorder(stockType) }"
                @mouseover="showTooltip[stockType] = !showTooltip[stockType]"
                @mouseleave="showTooltip[stockType] = false"
              >{{ $t(stockType) }}</span>
              <Transition name="fade">
                <div
                  v-show="showTooltip[stockType] && stockType === 'transit' && !checkPreorder(stockType)"
                  class="transit-tooltip on-transit"
                >
                  <div>{{ product.amount_free.transit | integerPart }} {{ $t('pcs_transit') }}</div>
                  <template v-if="product.shipment_date"> {{ $t('expected_shipment_date') }}: {{ product.shipment_date }} </template>
                  <span v-else>
                    <!-- {{ $t('delivery_deadline') }} -->
                    {{ transitDaysLimit }}
                    {{ transitDays }}
                    {{ transitDays | ending([$t('day_1'), $t('day_2'), $t('day_3')], $i18n.locale) }}
                  </span>
                </div>
              </Transition>
              <Transition name="fade">
                <div
                  v-show="showTooltip[stockType] && stockType === 'preorder' && !checkPreorder(stockType)"
                  class="transit-tooltip on-order"
                >
                  <span>
                    <!-- {{ $t('delivery_deadline') }} -->
                    {{ preorderDaysLimit }}
                    {{ preorderDays }}
                    {{ preorderDays | ending([$t('day_1'), $t('day_2'), $t('day_3')], $i18n.locale) }}
                  </span>
                </div>
              </Transition>
            </template>
          </div>

          <!-- Наличие -->
          <div class="availability">
            <!-- <template v-if="!$hideStockAvailabilityColumns()"> -->
            <template v-if="showStockAvailabilityColumns()">
              <template v-if="product.price > 0 && stockType !== 'preorder'">
                <div v-if="product.amount_free[stockType] > 0">
                  <span>{{ product.amount_free[stockType] | integerPart }}</span>
                  <span class="hide-desktop">{{ $t('pcs') }}</span>
                </div>
              </template>
              <span v-else>{{ $t('preorder') }}</span>
            </template>
          </div>

          <!-- Цена -->
          <div class="price">
            <!-- <div class="title-col hide-desktop">{{ $t('price_rub') }}</div> -->
            <div v-if="showControls(stockType) && (product.amount_free[stockType] > 0 || stockType === 'preorder')">
              <span class="price-digits" v-if="product.price > 0">
                {{ product.price | integerPart }}
                <span class="small">{{ product.price | fractionalPart }}</span>
                <span class="hide-desktop">{{ $t('rub') }}</span>
              </span>
              <span v-else>{{ $t('on_request') }}</span>
            </div>
          </div>

          <!-- Кол-во -->
          <div class="order order-qnt-edit">
            <div v-if="showControls(stockType) && !$noCartMode()">
              <a href="#" @click.prevent="decreaseAmount"><i class="svg-icon minus icon-pointer" /></a>
              <input-number ref="input-number" v-model="amountCart" :value="amountCart" :min-value="0" />
              <a href="#" @click.prevent="increaseAmount"><i class="svg-icon plus icon-pointer" /></a>
            </div>
          </div>

          <!-- Кнопки -->
          <div class="cart-col">
            <button
              v-if="showControls(stockType) && !$noCartMode() && product.price > 0"
              :aria-label="$t('to_cart')"
              @click.prevent="addCart"
              :class="{ 'disabled': amountCart < 1, 'product-cart-exists': productAmountCart > 0 }"
            >
              <i class="svg-icon cart-icon" /><span v-if="productAmountCart > 0">{{ productAmountCart | integerPart }}</span>
            </button>
            <button
              v-else-if="showControls(stockType) && !$noCartMode()"
              @click.prevent="addPriceRequest"
              :aria-label="$t('request_price')"
              :class="{ 'disabled': amountCart < 1, 'product-cart-exists': product.amount_price_request > 0 }"
            >
              <i class="svg-icon preorder" />
              <span v-if="product.amount_price_request > 0">{{ product.amount_price_request | integerPart }}</span>
            </button>
          </div>
        </template>
      </template>

      <!-- Показываем склады поставщиков, если есть -->
      <template v-if="canShowSuppliers(product)">
        <template v-for="(supplier, idx_supplier) in product.suppliers">
          <products-list-item-supplier
            v-if="!hasOwn(product) || supplier.stock.is_force_visible"
            :supplier="supplier"
            :product="product"
            :search-history="searchHistory"
            :search-term="searchTerm"
            :initial-amount="idx_supplier === 0 && !hasOwn(product) ? initialAmount : 0"
            :can-show-pack-warning="canShowPackWarning"
            ref="supplier_list"
          />
        </template>
      </template>
    </div>
  </div>
</template>

<script>
import { ProductFavourite } from '@/models/product';
import InputNumber from '@/components/controls/input-number';
import ProductsListItemSupplier from '@/components/catalog/products-list-item-supplier';

export default {
  components: {
    InputNumber,
    ProductsListItemSupplier,
  },
  props: {
    product: { type: Object, default: {} },
    disabled: { type: Boolean, default: false },
    hidePreorder: { type: Boolean, default: false },
    hidePriceOnRequest: { type: Boolean, default: false },
    stocks: { type: Array, default: [] },
    initialAmount: { type: Number, default: 0 },
    searchTerm: { type: String, default: '' },
    searchHistory: { type: Object, default: () => {} },
    canShowPackWarning: { type: Boolean, default: false },
  },
  data() {
    return {
      amountCart: 0,
      // amountCartSupplier: {},
      showTransit: false,
      showPreorder: false,
      isFavourite: false,
      showSupplier: false,
      showTooltip: {},
      amountShow: true,
    };
  },
  computed: {
    vendor() {
      return process.env.VENDOR;
    },
    productDisabled() {
      return this.product.price <= 0;
    },
    productLocaleData() {
      return this.product?.product_translations?.find((x) => x.language.slug === this.$i18n.locale) || {};
    },
    productAmountCart() {
      return Object.values(this.product.amount_cart).reduce((a, b) => a + b, 0);
    },
    preorderDays() {
      return this.stocks.find((x) => x.stock_type === 'preorder')?.shipment_days;
    },
    transitDays() {
      return this.stocks.find((x) => x.stock_type === 'transit')?.shipment_days;
    },
    preorderDaysLimit() {
      const dl = this.stocks.find((x) => x.stock_type === 'preorder')?.shipment_days_limit;
      if (dl === 'gt') {
        return this.$t('delivery_deadline_from');
      } else if (dl === 'lt') {
        return this.$t('delivery_deadline_to');
      }
      return this.$t('delivery_deadline');
    },
    transitDaysLimit() {
      const dl = this.stocks.find((x) => x.stock_type === 'transit')?.shipment_days_limit;
      if (dl === 'gt') {
        return this.$t('delivery_deadline_from');
      } else if (dl === 'lt') {
        return this.$t('delivery_deadline_to');
      }
      return this.$t('delivery_deadline');
    },
  },
  created() {
    this.isFavourite = this.product.is_favourite;
    this.$set(this.showTooltip, 'transit', false);
    this.$set(this.showTooltip, 'preorder', false);
    if (this.initialAmount) {
      this.amountCart = this.initialAmount;
    }
  },
  methods: {
    showStockAvailabilityColumns() {
      return this.searchTerm || this.vendor !== 'wellfar' || this.$auth.loggedIn;
    },
    showControls(stockType) {
      if (stockType === 'available') {
        return this.product.amount_free[stockType] > 0;
      } else if (stockType === 'transit') {
        return this.product.amount_free[stockType] > 0 && !this.product.amount_free['available'];
      } else if (stockType === 'preorder' && !this.hidePreorder) {
        return !(this.product.amount_free && (this.product.amount_free['available'] > 0 || this.product.amount_free['transit'] > 0));
      }
    },
    hasOwn(product) {
      return product.amount_free?.available || product.amount_free?.transit;
    },
    canShowSuppliers(product) {
      return product.suppliers.length > 0 && 
            (!this.hasOwn(product) || product.suppliers.some((x) => x.stock.is_force_visible));
    },
    getSelectedSupplier(product) {
      if (product.suppliers.length > 0 && !(product.amount_free?.available || product.amount_free?.transit)) {
        return product.suppliers[0];
      }
      return {};
    },
    noWrapHyphen(value) {
      return value ? value.replace(/(\S+)-(\S+)/g, '<nobr>$1-$2</nobr>') : '';
    },
    checkExpress(stockType) {
      return this.express && ['transit', 'preorder'].includes(stockType);
    },
    checkPreorder(stockType) {
      const amt = (this.product.amount_free['available'] > 0 || this.product.amount_free['transit'] > 0) && stockType === 'preorder';
      return (this.hidePreorder && ['preorder'].includes(stockType)) || amt;
    },
    decreaseAmount() {
      if (this.amountCart > 0) {
        let amount = this.amountCart - this.product.package_quantity;
        if (amount < 0) {
          amount = 0;
        }
        if (amount % this.product.package_quantity) {
          amount += this.product.package_quantity - (amount % this.product.package_quantity);
        }
        this.amountCart = amount;
      }
    },
    increaseAmount() {
      let amount = this.amountCart + this.product.package_quantity;
      amount -= amount % this.product.package_quantity;
      this.amountCart = amount;
    },
    addCart() {
      const data = {
        product: this.product.id,
        amount: this.amountCart,
        express: this.express,
        hide_preorder: this.hidePreorder,
      };
      if (this.searchHistory && this.searchTerm) {
        data.search_history = this.searchHistory.id;
        data.search_term = this.searchTerm;
      }
      this.$axios
        .$post('/order/update-cart/', data)
        // .$post('/order/add-cart/', data)
        .then((response) => {
          this.$store.dispatch('setCartExists');
          if (!response.amount_free && (this.express || this.hidePreorder)) {
            this.$emit('fetch-products');
          } else {
            this.product.amount_free = response.amount_free_dict;
            this.product.amount_cart = response.amount_cart_dict;
          }
          this.amountCart = 0;
          this.$notify({
            duration: 1000,
            text: this.$t('product_added_to_cart'),
          });
        })
        .catch((e) => {
          let msg = this.$t('unknown_error');
          try {
            msg = e.response.data.detail;
          } catch (e) {
          } finally {
            this.$notify({
              duration: 5000,
              text: msg,
            });
          }
        });
    },
    addPriceRequest() {
      const data = {
        product: this.product.id,
        amount: this.amountCart,
      };
      this.$axios
        .$post('/order/price-request/add-price-request/', data)
        .then((response) => {
          this.$store.dispatch('setPriceRequestExists');
          this.$emit('show-price-request-modal');
          this.product.amount_price_request = response.amount_price_request;
          this.amountCart = 0;
        })
        .catch((e) => {
          let msg = this.$t('unknown_error');
          try {
            msg = e.response.data.detail;
          } catch (e) {
          } finally {
            this.$notify({
              duration: 5000,
              text: msg,
            });
          }
        });
    },
    switchFavourite() {
      if (this.$auth.loggedIn) {
        let fav = new ProductFavourite({ product: this.product.id });
        if (this.isFavourite) {
          fav.id = this.product.id;
          fav.delete().then(() => {
            this.$store.dispatch('setFavouriteProductsExists');
            this.isFavourite = false;
            this.$notify({
              duration: 1000,
              text: this.$t('product_removed_from_fav'),
            });
            this.$emit('remove-favourite');
          });
        } else {
          fav.save().then(() => {
            this.$store.dispatch('setFavouriteProductsExists');
            this.isFavourite = true;
            this.$notify({
              duration: 1000,
              text: this.$t('product_added_to_fav'),
            });
          });
        }
      }
    },
    showTransitFalse() {
      this.showTransit = false;
    },
    showPreorderFalse() {
      this.showPreorder = false;
    },
  },
};
</script>

<i18n lang="yaml">
ru:
  available: 'В наличии'
  transit: 'В пути'
  remote: 'Производство'
  preorder: 'Под заказ'
  to_cart: 'В корзину'
  stock_pcs: 'Склад, шт.'
  no: 'Нет'
  request_price: 'Запросить цену'
en:
  available: 'In stock'
  transit: 'In transit'
  remote: 'Remote'
  preorder: 'Preorder'
  to_cart: 'Add to cart'
  stock_pcs: 'Stock, pcs.'
  no: 'No'
  request_price: 'Request a price'
</i18n>
